import React from "react";
import RegisterPage, { STATUS_BADGES, PRIORITY_BADGES, StatCard } from "../components/RegisterPage";
import { useHSE } from "../lib/store";
import { calculateClosurePercent } from "../lib/hseCalc";

const SEV = {
  Minor: { bg: "#DCFCE7", color: "#166534" },
  Major: { bg: "#FEF3C7", color: "#854D0E" },
  Critical: { bg: "#FEE2E2", color: "#7F1D1D" },
};

export default function NCRRegister() {
  const { data, siteNames } = useHSE();
  const rows = data.ncrs || [];
  const open = rows.filter((n) => n.status === "Open" || n.status === "In Progress").length;
  const closed = rows.filter((n) => n.status === "Closed").length;
  const overdue = rows.filter((n) => n.status === "Overdue").length;
  const pct = calculateClosurePercent(closed, rows.length);

  const Top = (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <StatCard label="Total NCR" value={rows.length} testid="ncr-total" />
      <StatCard label="Open" value={open} tone="warning" testid="ncr-open" />
      <StatCard label="Closed" value={closed} tone="safe" testid="ncr-closed" />
      <StatCard label="Overdue" value={overdue} tone="critical" testid="ncr-overdue" />
      <StatCard label="Closure %" value={`${pct}%`} tone={pct >= 85 ? "safe" : "warning"} sub="Target 85%" testid="ncr-pct" />
    </div>
  );

  return (
    <RegisterPage
      title="NCR Register"
      subtitle="Non-conformance reports — audit · client · consultant · internal · inspection · incident"
      storeKey="ncrs"
      idPrefix="NCR"
      topPanels={Top}
      columns={[
        { key: "id", label: "NCR ID" },
        { key: "dateReceived", label: "Received" },
        { key: "issuedBy", label: "Issued By" },
        { key: "issuedTo", label: "Issued To" },
        { key: "site", label: "Site" },
        { key: "contractor", label: "Contractor" },
        { key: "source", label: "Source" },
        { key: "category", label: "Category" },
        { key: "requirementViolated", label: "Requirement" },
        { key: "severity", label: "Severity" },
        { key: "responsible", label: "Responsible" },
        { key: "dueDate", label: "Due" },
        { key: "status", label: "Status" },
        { key: "verification", label: "Verified", render: (r) => r.verification ? <span className="text-[11px] text-[#18B84F]">✓ {r.verification}</span> : <span className="text-slate-300 text-[11px]">Pending</span> },
      ]}
      badgeMap={{ severity: SEV, status: STATUS_BADGES }}
      formFields={[
        { key: "id", label: "NCR ID" },
        { key: "dateReceived", label: "Date Received", type: "date" },
        { key: "issuedBy", label: "Issued By" },
        { key: "issuedTo", label: "Issued To" },
        { key: "site", label: "Site", type: "select", options: siteNames },
        { key: "department", label: "Department", type: "select", options: data.settings.departments },
        { key: "contractor", label: "Contractor / Department", type: "select", options: data.settings.contractors },
        { key: "source", label: "Source", type: "select", options: ["Internal Audit", "External Audit", "Client", "Consultant", "Inspection", "Incident"] },
        { key: "category", label: "NCR Category", type: "select", options: data.settings.ncrCategories },
        { key: "requirementViolated", label: "Requirement Violated" },
        { key: "description", label: "Description", type: "textarea", full: true },
        { key: "rootCause", label: "Root Cause", type: "textarea", full: true },
        { key: "correction", label: "Correction (Immediate)", type: "textarea", full: true },
        { key: "correctiveAction", label: "Corrective Action", type: "textarea", full: true },
        { key: "preventiveAction", label: "Preventive Action", type: "textarea", full: true },
        { key: "severity", label: "Severity", type: "select", options: ["Minor", "Major", "Critical"] },
        { key: "responsible", label: "Responsible Person" },
        { key: "dueDate", label: "Due Date", type: "date" },
        { key: "closureDate", label: "Closure Date", type: "date" },
        { key: "verification", label: "Verified By" },
        { key: "approval", label: "Approved By" },
        { key: "status", label: "Status", type: "select", options: ["Open", "In Progress", "Closed", "Overdue"] },
      ]}
    />
  );
}
